/**
 * CatalogImportModal — Excel'den katalog ürünü içe aktarma.
 *
 * 3 adım: sütun eşleştirme → önizleme → sonuç.
 *
 * InventoryImportModal'in sadeleştirilmiş hâli. Orijinali 3553 satır çünkü depo, marka,
 * para birimi, ÖTV, e-ticaret ve görsel yükleme gibi tenant kavramlarını taşıyor;
 * katalogda bunların hiçbiri yok. FİYAT SÜTUNU DA YOK — dosyada a_fiyat/s_fiyat bulunsa
 * bile eşleştirme listesinde görünmez.
 *
 * Barkod ana DB'de UNIQUE olduğu için çakışmalar önizlemede yakalanır; kullanıcı atlamayı
 * veya mevcut kaydı güncellemeyi seçer. Backend `bulk-create` de aynı kontrolü yapıp
 * eleyerek raporlar, yani batch hiçbir durumda patlamaz.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import * as XLSX from 'xlsx';

import CatalogService from '../../../../services/CatalogService';
import GuideButton from '../../../common/guide/GuideButton';
import { CATALOG_VAT_STATUS_OPTIONS } from '../types/catalog.types';
import type {
  CatalogBulkFailure,
  CatalogCategory,
  CatalogProductPayload,
  CatalogSector,
} from '../types/catalog.types';

// ════════════════════════════════════════════════════════════════
// Sabitler
// ════════════════════════════════════════════════════════════════

/** Backend tek çağrıda 2000'e kadar kabul ediyor; 500 hem hızlı hem güvenli. */
const BATCH_SIZE = 500;

const DEFAULT_UNIT = 'Adet';
const DEFAULT_VAT_RATE = 20;
const DEFAULT_VAT_STATUS = CATALOG_VAT_STATUS_OPTIONS[0];

/** Sorunlu satır tablolarında en fazla kaç satır gösterilecek. */
const PROBLEM_PREVIEW_LIMIT = 50;

/**
 * SheetJS hücreleri ham değerleriyle okunur (raw: true).
 *
 * raw:false görünen metni verdiği için sayı olarak kaydedilmiş 13 haneli bir barkodu
 * hücre biçimine göre "8.68351E+12" diye döndürüyor; bu hâliyle hem katalogla eşleşme
 * kontrolü tutmaz hem de kayda bozuk barkod girer. raw:true ise sayıyı olduğu gibi
 * verir (String ile tam basamaklara açılır) ve metin hücrelerdeki baştaki sıfırları
 * korur. Barkodlar 13 hane, yani Number.MAX_SAFE_INTEGER sınırının çok altında.
 */
const READ_RAW = true;

type TargetField =
  | 'name'
  | 'sector'
  | 'barcode'
  | 'catalogCode'
  | 'category1'
  | 'category2'
  | 'vatRate'
  | 'vatStatus'
  | 'unit'
  | 'departmentNo';

interface FieldDef {
  key: TargetField;
  label: string;
  required: boolean;
  /** Otomatik eşleştirme için başlık adayları (normalize edilmiş hâlleriyle karşılaştırılır). */
  aliases: string[];
}

const FIELD_DEFS: FieldDef[] = [
  { key: 'name', label: 'Ürün Adı', required: true, aliases: ['stokadi', 'urunadi', 'ad', 'urun', 'stok'] },
  { key: 'sector', label: 'Sektör', required: true, aliases: ['sektor'] },
  { key: 'barcode', label: 'Barkod', required: false, aliases: ['barkod', 'barcode'] },
  { key: 'catalogCode', label: 'Katalog Kodu', required: false, aliases: ['stokkodu', 'katalogkodu', 'kod'] },
  { key: 'category1', label: 'Kategori 1', required: false, aliases: ['kategori1', 'kategori', 'ustkategori'] },
  { key: 'category2', label: 'Kategori 2', required: false, aliases: ['kategori2', 'altkategori'] },
  { key: 'vatRate', label: 'KDV Oranı', required: false, aliases: ['kdvoran', 'kdvorani', 'kdv'] },
  { key: 'vatStatus', label: 'KDV Durumu', required: false, aliases: ['kdvdurumu'] },
  { key: 'unit', label: 'Birim', required: false, aliases: ['birim', 'olcubirimi'] },
  { key: 'departmentNo', label: 'ÖKC Departman', required: false, aliases: ['okc', 'departman', 'departmanno'] },
];

/** Örnek şablonun başlıkları — kullanıcı boş dosyayı indirip doldurabilsin. */
const TEMPLATE_HEADERS = [
  'stokAdi',
  'sektor',
  'BARKOD',
  'stokKodu',
  'kategori1',
  'kategori2',
  'kdv_oran',
  'kdv durumu',
  'birim',
  'okc',
];

// ════════════════════════════════════════════════════════════════
// Yardımcılar
// ════════════════════════════════════════════════════════════════

/**
 * Türkçe karaktere ve noktalama farkına duyarsız karşılaştırma anahtarı.
 * 'İ' JS'te toLowerCase ile birleşik noktalı 'i' ürettiği için önce sadeleştiriliyor.
 */
function norm(value: unknown): string {
  return String(value ?? '')
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .replace(/ı/g, 'i')
    .toLowerCase()
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '');
}

const text = (value: unknown): string => String(value ?? '').trim();

interface ParsedRow {
  /** Excel'deki satır numarası (başlık 1. satır) — hata logunda kullanılır. */
  rowNo: number;
  name: string;
  sector: string;
  barcode: string;
  catalogCode: string;
  category1: string;
  category2: string;
  vatRate: number;
  vatStatus: string;
  unit: string;
  departmentNo: number | null;
}

interface RowProblem {
  row: ParsedRow;
  reason: string;
}

interface Conflict {
  row: ParsedRow;
  existingId: number;
}

interface Validation {
  /** Doğrudan kaydedilecek satırlar (çakışmasız ve eksiksiz). */
  ready: ParsedRow[];
  invalid: RowProblem[];
  fileDuplicates: RowProblem[];
  conflicts: Conflict[];
  newSectors: string[];
  newCategories: string[];
}

interface ImportProgress {
  phase: string;
  current: number;
  total: number;
}

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  failed: CatalogBulkFailure[];
  conflicts: Conflict[];
}

export interface CatalogImportModalProps {
  open: boolean;
  onClose: () => void;
  sectors: CatalogSector[];
  /** İçe aktarma bitince ana sayfa listeyi ve sektörleri tazelesin. */
  onCompleted: () => void;
}

const CatalogImportModal: React.FC<CatalogImportModalProps> = ({
  open,
  onClose,
  sectors,
  onCompleted,
}) => {
  const [step, setStep] = useState(1); // 1: Eşleştirme, 2: Önizleme, 3: Sonuç
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<string[][]>([]);
  /** Hedef alan → kaynak sütun indeksi. -1 = eşleştirilmedi. */
  const [mapping, setMapping] = useState<Record<TargetField, number>>(
    () => Object.fromEntries(FIELD_DEFS.map((f) => [f.key, -1])) as Record<TargetField, number>
  );

  const [validation, setValidation] = useState<Validation | null>(null);
  const [conflictStrategy, setConflictStrategy] = useState<'skip' | 'update'>('skip');
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const resetAll = useCallback(() => {
    setStep(1);
    setError(null);
    setBusy(false);
    setFileName('');
    setSheetNames([]);
    setSelectedSheet('');
    setWorkbook(null);
    setHeaders([]);
    setSampleRows([]);
    setMapping(Object.fromEntries(FIELD_DEFS.map((f) => [f.key, -1])) as Record<TargetField, number>);
    setValidation(null);
    setConflictStrategy('skip');
    setProgress(null);
    setResult(null);
  }, []);

  const handleClose = () => {
    // İçe aktarma yapıldıysa ana liste bayat kalmasın.
    if (result && (result.created > 0 || result.updated > 0)) onCompleted();
    resetAll();
    onClose();
  };

  // ─── Dosya okuma ve otomatik eşleştirme ───────────
  const applySheet = useCallback((wb: XLSX.WorkBook, sheetName: string) => {
    const worksheet = wb.Sheets[sheetName];
    const matrix = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      defval: '',
      raw: READ_RAW,
      blankrows: false,
    });

    if (matrix.length === 0) {
      setHeaders([]);
      setSampleRows([]);
      setError('Seçilen sayfa boş.');
      return;
    }

    const headerRow = (matrix[0] as any[]).map((h) => text(h));
    setHeaders(headerRow);
    setSampleRows((matrix.slice(1, 4) as any[][]).map((r) => r.map((c) => text(c))));

    // Otomatik eşleştirme: başlık adı alan takma adlarından biriyle örtüşüyorsa bağla.
    const normalizedHeaders = headerRow.map(norm);
    const auto = Object.fromEntries(FIELD_DEFS.map((f) => [f.key, -1])) as Record<
      TargetField,
      number
    >;
    FIELD_DEFS.forEach((field) => {
      const exact = normalizedHeaders.findIndex((h) => h && field.aliases.includes(h));
      if (exact >= 0) {
        auto[field.key] = exact;
        return;
      }
      // Tam eşleşme yoksa içerme ile dene ("Stok Adı (TR)" gibi başlıklar için).
      const partial = normalizedHeaders.findIndex(
        (h) => h && field.aliases.some((alias) => h.includes(alias))
      );
      if (partial >= 0) auto[field.key] = partial;
    });

    // Aynı sütun iki alana bağlanmasın; ilk sahiplenen kalır.
    const used = new Set<number>();
    FIELD_DEFS.forEach((field) => {
      const index = auto[field.key];
      if (index < 0) return;
      if (used.has(index)) auto[field.key] = -1;
      else used.add(index);
    });

    setMapping(auto);
    setError(null);
  }, []);

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames);
        const first = wb.SheetNames[0];
        setSelectedSheet(first);
        applySheet(wb, first);
      } catch (err) {
        console.error('Excel okunamadı:', err);
        setError('Dosya okunamadı. Geçerli bir Excel dosyası seçtiğinizden emin olun.');
      }
    };
    reader.readAsArrayBuffer(file);

    // Aynı dosya tekrar seçilebilsin diye input sıfırlanır.
    event.target.value = '';
  };

  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (workbook) applySheet(workbook, sheetName);
  };

  const mappingReady = useMemo(
    () => FIELD_DEFS.filter((f) => f.required).every((f) => mapping[f.key] >= 0),
    [mapping]
  );

  // ─── Adım 1 → 2: satırları çöz ve doğrula ─────────
  const handleValidate = async () => {
    if (!workbook || !selectedSheet) return;

    setBusy(true);
    setError(null);
    try {
      const worksheet = workbook.Sheets[selectedSheet];
      const matrix = XLSX.utils.sheet_to_json<any[]>(worksheet, {
        header: 1,
        defval: '',
        raw: READ_RAW,
        blankrows: false,
      });

      const cell = (row: any[], field: TargetField): string => {
        const index = mapping[field];
        return index >= 0 ? text(row[index]) : '';
      };

      const rows: ParsedRow[] = [];
      for (let i = 1; i < matrix.length; i += 1) {
        const raw = matrix[i] as any[];
        // Tamamen boş satırları sessizce atla.
        if (!raw || raw.every((c) => text(c) === '')) continue;

        const vatRateText = cell(raw, 'vatRate').replace(',', '.').replace('%', '');
        const parsedVat = Number.parseFloat(vatRateText);
        const departmentText = cell(raw, 'departmentNo');
        const parsedDepartment = Number.parseInt(departmentText, 10);

        rows.push({
          rowNo: i + 1,
          name: cell(raw, 'name'),
          sector: cell(raw, 'sector'),
          barcode: cell(raw, 'barcode'),
          catalogCode: cell(raw, 'catalogCode'),
          category1: cell(raw, 'category1'),
          category2: cell(raw, 'category2'),
          vatRate: Number.isFinite(parsedVat) ? parsedVat : DEFAULT_VAT_RATE,
          vatStatus: cell(raw, 'vatStatus') || DEFAULT_VAT_STATUS,
          unit: cell(raw, 'unit') || DEFAULT_UNIT,
          departmentNo: Number.isFinite(parsedDepartment) ? parsedDepartment : null,
        });
      }

      if (rows.length === 0) {
        setError('Dosyada içe aktarılacak satır bulunamadı.');
        return;
      }

      // 1) Zorunlu alanlar
      const invalid: RowProblem[] = [];
      const candidates: ParsedRow[] = [];
      rows.forEach((row) => {
        if (!row.name) invalid.push({ row, reason: 'Ürün adı boş.' });
        else if (!row.sector) invalid.push({ row, reason: 'Sektör boş.' });
        else if (row.vatRate < 0 || row.vatRate > 100)
          invalid.push({ row, reason: 'KDV oranı 0-100 aralığında olmalı.' });
        else candidates.push(row);
      });

      // 2) Dosya içi mükerrer barkod — ilk satır kalır, sonrakiler elenir.
      const seen = new Map<string, number>();
      const fileDuplicates: RowProblem[] = [];
      const unique: ParsedRow[] = [];
      candidates.forEach((row) => {
        if (!row.barcode) {
          unique.push(row);
          return;
        }
        const key = norm(row.barcode);
        const firstRowNo = seen.get(key);
        if (firstRowNo !== undefined) {
          fileDuplicates.push({
            row,
            reason: `Bu barkod dosyada ${firstRowNo}. satırda da geçiyor.`,
          });
          return;
        }
        seen.set(key, row.rowNo);
        unique.push(row);
      });

      // 3) Ana DB'de zaten var olan barkodlar
      const existingMap = await CatalogService.checkBarcodes(
        unique.map((r) => r.barcode).filter(Boolean)
      );
      // Backend haritayı katalogdaki barkodla anahtarlıyor; eşleşme SQL tarafında
      // yapıldığı için harf büyüklüğü dosyadakinden farklı olabilir. Normalize edilmiş
      // anahtarla aranırsa çakışma sessizce kaçmaz.
      const existingByKey = new Map<string, number>(
        Object.entries(existingMap).map(([barcode, id]) => [norm(barcode), id])
      );

      const conflicts: Conflict[] = [];
      const ready: ParsedRow[] = [];
      unique.forEach((row) => {
        const existingId = row.barcode ? existingByKey.get(norm(row.barcode)) : undefined;
        if (existingId) conflicts.push({ row, existingId });
        else ready.push(row);
      });

      // 4) Oluşturulacak yeni sektör ve kategoriler
      const allCategories = await CatalogService.getCategories();
      const sectorByName = new Map(sectors.map((s) => [norm(s.name), s]));
      const categoryKeys = new Set(
        allCategories.map((c) => `${c.sectorId}|${c.parentId ?? 0}|${norm(c.name)}`)
      );

      const newSectors = new Set<string>();
      const newCategories = new Set<string>();
      // Yeni sektörlere sanal negatif Id verilir; onların altındaki her kategori de yenidir.
      const virtualSectorIds = new Map<string, number>();
      let virtualId = -1;

      [...ready, ...conflicts.map((c) => c.row)].forEach((row) => {
        const sectorKey = norm(row.sector);
        const known = sectorByName.get(sectorKey);
        let sectorId: number;

        if (known) {
          sectorId = known.id;
        } else {
          newSectors.add(row.sector);
          if (!virtualSectorIds.has(sectorKey)) {
            virtualSectorIds.set(sectorKey, virtualId);
            virtualId -= 1;
          }
          sectorId = virtualSectorIds.get(sectorKey)!;
        }

        if (row.category1) {
          const key = `${sectorId}|0|${norm(row.category1)}`;
          if (!categoryKeys.has(key)) {
            categoryKeys.add(key);
            newCategories.add(`${row.sector} > ${row.category1}`);
          }
        }
        if (row.category2) {
          // Alt kategorinin üstü kategori1; yoksa kendisi kök olur.
          const parent = row.category1
            ? allCategories.find(
                (c) =>
                  c.sectorId === sectorId && !c.parentId && norm(c.name) === norm(row.category1)
              )
            : undefined;
          // Üst kategori henüz yoksa adıyla anahtarlanır; farklı yeni üstler altındaki
          // aynı isimli alt kategoriler tek sayılmasın.
          const parentKey = parent
            ? parent.id
            : row.category1
              ? `yeni:${norm(row.category1)}`
              : 0;
          const key = `${sectorId}|${parentKey}|${norm(row.category2)}`;
          if (!categoryKeys.has(key)) {
            categoryKeys.add(key);
            newCategories.add(
              row.category1
                ? `${row.sector} > ${row.category1} > ${row.category2}`
                : `${row.sector} > ${row.category2}`
            );
          }
        }
      });

      setValidation({
        ready,
        invalid,
        fileDuplicates,
        conflicts,
        newSectors: Array.from(newSectors),
        newCategories: Array.from(newCategories),
      });
      setStep(2);
    } catch (err: any) {
      console.error('Doğrulama başarısız:', err);
      setError(err?.message || 'Dosya doğrulanamadı.');
    } finally {
      setBusy(false);
    }
  };

  // ─── Adım 2 → 3: kaydet ───────────────────────────
  const handleImport = async () => {
    if (!validation) return;

    setBusy(true);
    setError(null);
    try {
      const toCreate = validation.ready;
      const toUpdate = conflictStrategy === 'update' ? validation.conflicts : [];

      // Sektör ve kategori önbellekleri: get-or-create çağrıları tekrarlanmasın.
      const sectorCache = new Map<string, number>(sectors.map((s) => [norm(s.name), s.id]));
      const allCategories: CatalogCategory[] = await CatalogService.getCategories();
      const categoryCache = new Map<string, number>(
        allCategories.map((c) => [`${c.sectorId}|${c.parentId ?? 0}|${norm(c.name)}`, c.id])
      );

      const ensureSector = async (name: string): Promise<number> => {
        const key = norm(name);
        const cached = sectorCache.get(key);
        if (cached !== undefined) return cached;
        const created = await CatalogService.createSector(name);
        sectorCache.set(key, created.id);
        return created.id;
      };

      const ensureCategory = async (
        sectorId: number,
        parentId: number | null,
        name: string
      ): Promise<number> => {
        const key = `${sectorId}|${parentId ?? 0}|${norm(name)}`;
        const cached = categoryCache.get(key);
        if (cached !== undefined) return cached;
        const created = await CatalogService.createCategory({ sectorId, parentId, name });
        categoryCache.set(key, created.id);
        return created.id;
      };

      const allRows = [...toCreate, ...toUpdate.map((c) => c.row)];

      // 1) Sektörler
      const distinctSectors = Array.from(new Set(allRows.map((r) => r.sector)));
      setProgress({ phase: 'Sektörler hazırlanıyor', current: 0, total: distinctSectors.length });
      for (let i = 0; i < distinctSectors.length; i += 1) {
        await ensureSector(distinctSectors[i]);
        setProgress({
          phase: 'Sektörler hazırlanıyor',
          current: i + 1,
          total: distinctSectors.length,
        });
      }

      // 2) Kategoriler — kökten yaprağa çözülür, ürüne EN DERİN seviye yazılır.
      setProgress({ phase: 'Kategoriler hazırlanıyor', current: 0, total: allRows.length });
      const categoryIdByRow = new Map<number, number | null>();
      for (let i = 0; i < allRows.length; i += 1) {
        const row = allRows[i];
        const sectorId = sectorCache.get(norm(row.sector))!;

        let level1: number | null = null;
        if (row.category1) level1 = await ensureCategory(sectorId, null, row.category1);

        let level2: number | null = null;
        if (row.category2) level2 = await ensureCategory(sectorId, level1, row.category2);

        categoryIdByRow.set(row.rowNo, level2 ?? level1);
        if (i % 25 === 0 || i === allRows.length - 1) {
          setProgress({
            phase: 'Kategoriler hazırlanıyor',
            current: i + 1,
            total: allRows.length,
          });
        }
      }

      const toPayload = (row: ParsedRow): CatalogProductPayload => ({
        name: row.name,
        barcode: row.barcode || null,
        catalogCode: row.catalogCode || null,
        sectorId: sectorCache.get(norm(row.sector))!,
        categoryId: categoryIdByRow.get(row.rowNo) ?? null,
        vatRate: row.vatRate,
        vatStatus: row.vatStatus,
        unit: row.unit,
        departmentNo: row.departmentNo,
        isActive: true,
      });

      // 3) Ürünler — 500'lük batch'ler
      let created = 0;
      const failed: CatalogBulkFailure[] = [];

      for (let i = 0; i < toCreate.length; i += BATCH_SIZE) {
        const batch = toCreate.slice(i, i + BATCH_SIZE);
        setProgress({
          phase: 'Ürünler kaydediliyor',
          current: i,
          total: toCreate.length,
        });

        const response = await CatalogService.bulkCreateProducts(batch.map(toPayload));
        created += response.successCount ?? 0;
        if (response.failedItems?.length) failed.push(...response.failedItems);
      }
      if (toCreate.length > 0) {
        setProgress({
          phase: 'Ürünler kaydediliyor',
          current: toCreate.length,
          total: toCreate.length,
        });
      }

      // 4) Çakışan kayıtların güncellenmesi — toplu ucu yok, satır satır gider.
      let updated = 0;
      for (let i = 0; i < toUpdate.length; i += 1) {
        const { row, existingId } = toUpdate[i];
        setProgress({ phase: 'Mevcut kayıtlar güncelleniyor', current: i, total: toUpdate.length });
        try {
          await CatalogService.updateProduct({ ...toPayload(row), id: existingId });
          updated += 1;
        } catch (err: any) {
          failed.push({
            name: row.name,
            barcode: row.barcode,
            reason: err?.message || 'Güncelleme başarısız.',
          });
        }
      }

      setResult({
        created,
        updated,
        skipped: conflictStrategy === 'skip' ? validation.conflicts.length : 0,
        failed: [
          ...failed,
          ...validation.invalid.map((p) => ({
            name: p.row.name,
            barcode: p.row.barcode,
            reason: p.reason,
          })),
          ...validation.fileDuplicates.map((p) => ({
            name: p.row.name,
            barcode: p.row.barcode,
            reason: p.reason,
          })),
        ],
        conflicts: validation.conflicts,
      });
      setProgress(null);
      setStep(3);
    } catch (err: any) {
      console.error('İçe aktarma başarısız:', err);
      setError(err?.message || 'İçe aktarma sırasında hata oluştu.');
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  // ─── Excel çıktıları ──────────────────────────────
  const handleDownloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, 'Katalog');
    XLSX.writeFile(wb, 'katalog_ice_aktarma_sablonu.xlsx');
  };

  const handleDownloadErrorLog = () => {
    if (!result) return;

    const wb = XLSX.utils.book_new();

    const failedSheet = XLSX.utils.json_to_sheet(
      result.failed.map((item) => ({
        'Ürün Adı': item.name,
        Barkod: item.barcode ?? '',
        Gerekçe: item.reason,
      }))
    );
    XLSX.utils.book_append_sheet(wb, failedSheet, 'Başarısız Ürünler');

    const conflictSheet = XLSX.utils.json_to_sheet(
      result.conflicts.map(({ row, existingId }) => ({
        'Excel Satırı': row.rowNo,
        'Ürün Adı': row.name,
        Barkod: row.barcode,
        'Katalogdaki Ürün Id': existingId,
        Sonuç: conflictStrategy === 'update' ? 'Mevcut kayıt güncellendi' : 'Atlandı',
      }))
    );
    XLSX.utils.book_append_sheet(wb, conflictSheet, 'Barkod Çakışmaları');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `katalog_ice_aktarma_logu_${timestamp}.xlsx`);
  };

  // ════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════

  const progressPercent =
    progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const renderProblemTable = (title: string, problems: RowProblem[]) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {title} ({problems.length.toLocaleString('tr-TR')})
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Satır</TableCell>
              <TableCell>Ürün Adı</TableCell>
              <TableCell sx={{ width: 140 }}>Barkod</TableCell>
              <TableCell>Gerekçe</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {problems.slice(0, PROBLEM_PREVIEW_LIMIT).map((problem, index) => (
              <TableRow key={`${problem.row.rowNo}-${index}`}>
                <TableCell>{problem.row.rowNo}</TableCell>
                <TableCell>{problem.row.name || '-'}</TableCell>
                <TableCell>{problem.row.barcode || '-'}</TableCell>
                <TableCell>{problem.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {problems.length > PROBLEM_PREVIEW_LIMIT && (
        <Typography variant="caption" color="text.secondary">
          İlk {PROBLEM_PREVIEW_LIMIT} satır gösteriliyor; tamamı hata logunda.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <FileUploadIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
          Excel'den İçe Aktar
        </Typography>
        <GuideButton guideKey="superadmin.catalogImport" size="small" />
        <IconButton size="small" onClick={handleClose} disabled={busy} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Sütun Eşleştirme</StepLabel>
          </Step>
          <Step>
            <StepLabel>Önizleme</StepLabel>
          </Step>
          <Step>
            <StepLabel>Sonuç</StepLabel>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {progress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {progress.phase} — {progress.current.toLocaleString('tr-TR')} /{' '}
              {progress.total.toLocaleString('tr-TR')}
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {/* ═══ Adım 1: Sütun eşleştirme ═══ */}
        {step === 1 && (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button variant="contained" component="label" startIcon={<FileUploadIcon />}>
                Dosya Seç
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelected}
                />
              </Button>
              <Button variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                Örnek Şablon İndir
              </Button>
              {fileName && <Chip label={fileName} onDelete={resetAll} />}
              {sheetNames.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={selectedSheet}
                    onChange={(e) => handleSheetChange(e.target.value)}
                  >
                    {sheetNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {headers.length === 0 ? (
              <Alert severity="info">
                Başlamak için bir Excel dosyası seçin. İlk satır sütun başlıkları olarak okunur.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Fiyat sütunları içe aktarılmaz.
                </Alert>

                <Grid container spacing={1.5}>
                  {FIELD_DEFS.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                      <Typography variant="caption" color="text.secondary">
                        {field.label}
                        {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={mapping[field.key]}
                          onChange={(e) =>
                            setMapping((prev) => ({ ...prev, [field.key]: Number(e.target.value) }))
                          }
                          error={field.required && mapping[field.key] < 0}
                        >
                          <MenuItem value={-1}>
                            <em>Eşleştirme yok</em>
                          </MenuItem>
                          {headers.map((header, index) => (
                            <MenuItem key={`${header}-${index}`} value={index}>
                              {header || `(sütun ${index + 1})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>

                {sampleRows.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Dosyanın ilk satırları
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {headers.map((header, index) => (
                              <TableCell key={`${header}-${index}`} sx={{ whiteSpace: 'nowrap' }}>
                                {header || `(sütun ${index + 1})`}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sampleRows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {headers.map((_, colIndex) => (
                                <TableCell key={colIndex} sx={{ whiteSpace: 'nowrap' }}>
                                  {row[colIndex] ?? ''}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {/* ═══ Adım 2: Önizleme ═══ */}
        {step === 2 && validation && (
          <Box>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6" color="success.main" fontWeight={700}>
                    {validation.ready.length.toLocaleString('tr-TR')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Eklenecek ürün
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6" color="warning.main" fontWeight={700}>
                    {validation.conflicts.length.toLocaleString('tr-TR')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Barkod çakışması
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6" color="error.main" fontWeight={700}>
                    {(validation.invalid.length + validation.fileDuplicates.length).toLocaleString(
                      'tr-TR'
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Atlanacak satır
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    {validation.newSectors.length} / {validation.newCategories.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Yeni sektör / kategori
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {(validation.newSectors.length > 0 || validation.newCategories.length > 0) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  {validation.newSectors.length} yeni sektör, {validation.newCategories.length} yeni
                  kategori oluşturulacak
                </AlertTitle>
                {validation.newSectors.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {validation.newSectors.slice(0, 20).map((name) => (
                      <Chip key={name} size="small" label={name} />
                    ))}
                    {validation.newSectors.length > 20 && (
                      <Chip size="small" label={`+${validation.newSectors.length - 20}`} />
                    )}
                  </Box>
                )}
                {validation.newCategories.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {validation.newCategories.slice(0, 20).map((name) => (
                      <Chip key={name} size="small" variant="outlined" label={name} />
                    ))}
                    {validation.newCategories.length > 20 && (
                      <Chip size="small" label={`+${validation.newCategories.length - 20}`} />
                    )}
                  </Box>
                )}
              </Alert>
            )}

            {validation.conflicts.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Barkodu katalogda zaten olan {validation.conflicts.length.toLocaleString('tr-TR')}{' '}
                  satır ne olsun?
                </Typography>
                <RadioGroup
                  row
                  value={conflictStrategy}
                  onChange={(e) => setConflictStrategy(e.target.value as 'skip' | 'update')}
                >
                  <FormControlLabel value="skip" control={<Radio size="small" />} label="Atla" />
                  <FormControlLabel
                    value="update"
                    control={<Radio size="small" />}
                    label="Mevcut kaydı güncelle"
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Güncelleme satır satır gittiği için çakışma sayısı yüksekse uzun sürebilir.
                </Typography>
              </Paper>
            )}

            {validation.invalid.length > 0 &&
              renderProblemTable('Zorunlu alanı eksik satırlar', validation.invalid)}
            {validation.fileDuplicates.length > 0 &&
              renderProblemTable('Dosya içinde mükerrer barkodlar', validation.fileDuplicates)}

            {validation.ready.length === 0 && validation.conflicts.length === 0 && (
              <Alert severity="warning">
                İçe aktarılabilecek satır yok. Sütun eşleştirmesini kontrol edin.
              </Alert>
            )}
          </Box>
        )}

        {/* ═══ Adım 3: Sonuç ═══ */}
        {step === 3 && result && (
          <Box>
            <Alert severity={result.failed.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              <AlertTitle>İçe aktarma tamamlandı</AlertTitle>
              {result.created.toLocaleString('tr-TR')} ürün eklendi
              {result.updated > 0 && `, ${result.updated.toLocaleString('tr-TR')} ürün güncellendi`}
              {result.skipped > 0 && `, ${result.skipped.toLocaleString('tr-TR')} çakışma atlandı`}
              {result.failed.length > 0 &&
                `, ${result.failed.length.toLocaleString('tr-TR')} satır kaydedilemedi`}
              .
            </Alert>

            {result.failed.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Kaydedilemeyen satırlar
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ürün Adı</TableCell>
                        <TableCell sx={{ width: 140 }}>Barkod</TableCell>
                        <TableCell>Gerekçe</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.failed.slice(0, PROBLEM_PREVIEW_LIMIT).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name || '-'}</TableCell>
                          <TableCell>{item.barcode || '-'}</TableCell>
                          <TableCell>{item.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {result.failed.length > PROBLEM_PREVIEW_LIMIT && (
                  <Typography variant="caption" color="text.secondary">
                    İlk {PROBLEM_PREVIEW_LIMIT} satır gösteriliyor; tamamı hata logunda.
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        {step === 1 && (
          <>
            <Button color="inherit" onClick={handleClose} disabled={busy}>
              İptal
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={busy || !mappingReady || headers.length === 0}
            >
              {busy ? 'Doğrulanıyor...' : 'Devam'}
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Button color="inherit" onClick={() => setStep(1)} disabled={busy}>
              Geri
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={
                busy ||
                !validation ||
                (validation.ready.length === 0 &&
                  !(conflictStrategy === 'update' && validation.conflicts.length > 0))
              }
            >
              {busy ? 'Aktarılıyor...' : 'İçe Aktar'}
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadErrorLog}
              disabled={!result || (result.failed.length === 0 && result.conflicts.length === 0)}
            >
              Hata Logunu İndir
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button color="inherit" onClick={resetAll}>
              Yeni Dosya
            </Button>
            <Button variant="contained" onClick={handleClose}>
              Kapat
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CatalogImportModal;
